import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HistoryTools from "../HistoryTools";
import { openPatientHistory } from "../tools/HistoryTool";
import EditModal from "../EditModal";
import "../../styles/Datatable.css";
import AssignDoctorModal from "../AssignDoctorModal";
import {generateWordReport} from '../../utils/GenerateWordDocument'
const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
const STATUS_TABS = ["ALL", "PENDING", "SIGNED OFF", "URGENT", "CRITICAL"];
const toComparableDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  if (ddmmyyyy.match(/^\d{4}-\d{2}-\d{2}$/)) return ddmmyyyy;
  if (ddmmyyyy.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [dd, mm, yyyy] = ddmmyyyy.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }
  return ddmmyyyy;
};


const normalizeRow = (row) => {
  const allStatuses = (row.studies ?? [])
    .map((s) => s.report_status ?? "")
    .filter(Boolean);
  return {
    ...row,
    patient: row.patient ?? row.patient_name ?? "",
    study_id: row.study_id ?? row.studies?.[0]?.id ?? "",
    study: row.study ?? row.studies?.[0]?.study_description ?? "",
    assigned: row.assigned ?? row.studies?.[0]?.assigned_doctor?.doctor_name ?? "",
    referral: row.referral ?? "",
    status: allStatuses.join(" "),
    allStatuses,
    center: row.center ?? "",
    studyDate: row.studyDate ?? "",
    studyDateComparable: toComparableDate(row.studyDate ?? ""),
    uploaded: row.uploaded ?? "",
    signedOff: row.signedOff ?? "",
    id: row.id != null ? String(row.id) : "",
  };
};

export default function StudyList({ studyData = [], rowsPerPage, setRowsPerPage, currentPage, setCurrentPage, onPatientSelect }) {

  const [activeTab, setActiveTab] = useState("ALL");
  const [colFilters, setColFilters] = useState({
    id: "", patient: "", study: "", referral: "", status: "",
    center: "", assigned: "",
    studyDateFrom: "", studyDateTo: "", uploadedDate: "", signedOffDate: "",
  });

  const [editPatient,       setEditPatient]       = useState(null);
  const [attachmentPatient, setAttachmentPatient] = useState(null);
  const [historyPatient,    setHistoryPatient]    = useState(null);
  const [commentsPatient,   setCommentsPatient]   = useState(null);
  const [userPatient,       setUserPatient]       = useState(null);
  const [openPatient,       setOpenPatient]       = useState(null);

  const navigate = useNavigate();
  const [assignModal,    setAssignModal]    = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  const updateFilter = (key, value) => {
    setColFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  const downloadReport = async (patient,report) => {
    try {
        await generateWordReport(
            patient,
            report.report_text,
            patient.report_status
        );

    } catch (err) {
        console.error(err);
        alert("Unable to download report.");
    }
};
  const filteredData = useMemo(() => {
    return (studyData ?? [])
      .map(normalizeRow)
      .filter((row) => {
        const tabMatch =
          activeTab === "ALL" ||
          row.allStatuses.some(
            (s) => s.toUpperCase() === activeTab.toUpperCase()
          );

        const f = colFilters;
        const fromOk = f.studyDateFrom === "" || row.studyDateComparable >= f.studyDateFrom;
        const toOk   = f.studyDateTo   === "" || row.studyDateComparable <= f.studyDateTo;
        const uploadedComp  = toComparableDate(row.uploaded);
        const signedOffComp = toComparableDate(row.signedOff);
        const uploadedOk    = f.uploadedDate  === "" || uploadedComp  >= f.uploadedDate;
        const signedOffOk   = f.signedOffDate === "" || signedOffComp >= f.signedOffDate;
        const statusMatch = f.status === "" ||
          row.status.toLowerCase().includes(f.status.toLowerCase());

        const colMatch =
          row.id.includes(f.id) &&
          row.patient.toLowerCase().includes(f.patient.toLowerCase()) &&
          row.study.toLowerCase().includes(f.study.toLowerCase()) &&
          row.referral.toLowerCase().includes(f.referral.toLowerCase()) &&
          statusMatch &&
          row.center.toLowerCase().includes(f.center.toLowerCase()) &&
          row.assigned.toLowerCase().includes(f.assigned.toLowerCase()) &&
          fromOk && toOk && uploadedOk && signedOffOk;

        return tabMatch && colMatch;
      });
  }, [activeTab, colFilters, studyData]);

  // Pagination
  const totalRows  = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const activePage = currentPage > totalPages ? totalPages : currentPage;
  const startIndex = (activePage - 1) * rowsPerPage;
  const endIndex   = Math.min(startIndex + rowsPerPage, totalRows);
  const paginatedData = filteredData.slice(startIndex, endIndex);
  console.log(paginatedData, 'patient');

  return (
    <>
      {assignModal && selectedPatient && (
        <AssignDoctorModal
          patient={selectedPatient}
          onClose={() => setAssignModal(false)}
          onAssigned={() => {
            setAssignModal(false);
            window.location.reload();
          }}
        />
      )}

      <div className="study-list-wrapper">
        {/* Modals */}
        {editPatient && <EditModal patient={editPatient} onClose={() => setEditPatient(null)} />}

        {/* Status Tabs */}
        <div className="status-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              className={`status-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Id ↓</th>
                <th>Patient</th>
                <th>Study</th>
                <th>Referral</th>
                <th>Status</th>
                <th>History</th>
                <th>tools</th>
                <th>Center</th>
                <th>Assigned to</th>
                <th>Study Date</th>
                <th>Uploaded</th>
                <th>SignedOff</th>
                <th>Report</th>
                {/* <th>Tag</th>
                <th>Actions</th> */}
              </tr>
              <tr className="filter-row">
                <th><input className="col-filter" value={colFilters.patient_id}       onChange={(e) => updateFilter("id",       e.target.value)} placeholder="ID"       /></th>
                <th><input className="col-filter" value={colFilters.patient}  onChange={(e) => updateFilter("patient",  e.target.value)} placeholder="Patient"  /></th>
                <th><input className="col-filter" value={colFilters.study}    onChange={(e) => updateFilter("study",    e.target.value)} placeholder="Study"    /></th>
                <th><input className="col-filter" value={colFilters.referral} onChange={(e) => updateFilter("referral", e.target.value)} placeholder="Referral" /></th>
                <th><input className="col-filter" value={colFilters.status}   onChange={(e) => updateFilter("status",   e.target.value)} placeholder="Status"   /></th>
                <th></th>
                <th></th>
                <th><input className="col-filter" value={colFilters.center}   onChange={(e) => updateFilter("center",   e.target.value)} placeholder="Center"   /></th>
                <th><input className="col-filter" value={colFilters.assigned} onChange={(e) => updateFilter("assigned", e.target.value)} placeholder="Assigned" /></th>
                <th>
                  <div className="date-filter-wrap">
                    <input type="date" className="col-filter col-filter-date" value={colFilters.studyDateFrom} onChange={(e) => updateFilter("studyDateFrom", e.target.value)} />
                    <input type="date" className="col-filter col-filter-date" value={colFilters.studyDateTo}   onChange={(e) => updateFilter("studyDateTo",   e.target.value)} />
                  </div>
                </th>
                <th><input type="date" className="col-filter col-filter-date" value={colFilters.uploadedDate}  onChange={(e) => updateFilter("uploadedDate",  e.target.value)} /></th>
                <th><input type="date" className="col-filter col-filter-date" value={colFilters.signedOffDate} onChange={(e) => updateFilter("signedOffDate", e.target.value)} /></th>
                <th></th><th></th><th></th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr><td colSpan={14} className="no-records">No records found</td></tr>
              ) : (
                paginatedData.map((row) => (
                  <tr key={row.patient_id}>
                    <td>{row.patient_id}</td>
                    <td>
                      <Link
                        to={`/viewer/${row.id}`}
                        state={{
                          compareData: row,
                          patient: {
                            patient_id: row.patient_id || row.id,
                            patient_name: row.patient || row.patient_name,
                          }
                        }}
                        className="patientLink"
                        onClick={() => {
                          console.log("Clicked Patient:", row);
                          console.log("Clicked Patient ID:", row.patient_id);
                          onPatientSelect?.({
                            patient_id: row.patient_id,
                            patient_name: row.patient,
                          });
                        }}
                      >
                        {row.patient}
                      </Link>
                    </td>
                    <td>{row.study}</td>
                    <td>{row.referral}</td>
                    <td>
                      {row.report_status ? (
                            <span
                              key={row.id}
                              className={`status-badge status-${row.report_status?.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              {row.report_status}
                            </span>
                          )
                        : "—"}
                    </td>
                    <td>
                    <button
                      className="history-main-btn"
                      onClick={() => openPatientHistory(row.patient_id)}
                    >
                      History
                    </button>
                    </td>
                    <HistoryTools
                      onEdit={() => setEditPatient(row)}
                    />
                    <td>{row.center}</td>
                    <td>
                      {(storedUser.role === "admin" || storedUser.role === "center" || storedUser.role === 'assigner') ? (
                        <div>
                          {row.assigned || "Not Assigned"}
                          <br />
                          <button
                            className="assign-btn"
                            onClick={() => {
                              setSelectedPatient(row);
                              setAssignModal(true);
                            }}
                          >
                            Assign To
                          </button>
                        </div>
                      ) : (storedUser.username)}
                    </td>
                    <td>{row.studyDate}</td>
                    <td>{row.uploaded}</td> 
                    <td>{row.signedOff}</td>
                    <td className="report-cell">
                    {row.reports?.length > 0 ? (
                      row.reports.map((report) => (
                        <div key={report.id}>
                          <span
                            className="report-link"
                            style={{ cursor: "pointer" }}
                            onClick={() => downloadReport(row,report)}
                          >
                            {report.report_name || "Default Report"}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span>No Report</span>
                    )}
                  </td>
                    {/* <td className="tag-cell">
                      <button className="tag-add-btn" title="Add tag">+</button>
                    </td>
                    <td className="actions-cell">
                      <button className="action-icon-btn download-btn" title="Download">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="8 17 12 21 16 17" />
                          <line x1="12" y1="12" x2="12" y2="21" />
                          <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" />
                        </svg>
                      </button>
                      <button className="action-icon-btn lock-btn" title="Lock">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </button>
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}